import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';
import { BillService } from '@api/bill.service';

export type BillJobStatus = 'queued' | 'processing' | 'needsConfirmation' | 'done' | 'error' | 'cancelled';

// what the UI renders for each bill in the queue
export interface BillUploadJob {
  id: number;
  fileName: string;
  siteId: number;
  status: BillJobStatus;
  billType: string | null;
  anyWritten: boolean;
  written: string[];
  warnings: string[];
  // informational, not a problem: things the bill simply didn't contain
  notices: string[];
  conflicts: string[];
  error: string | null;
}

// internal: the job + the File to send (never rendered) and, once extracted, the OCR
// body kept so a confirm-overwrite can commit without re-running OCR.
interface QueuedItem {
  job: BillUploadJob;
  file: File;
  extraction?: any;
}

@Injectable({ providedIn: 'root' })
export class BillUploadQueueService {
  private nextId = 1;
  private items: QueuedItem[] = [];
  private processing = false;

  private readonly jobsSource = new BehaviorSubject<BillUploadJob[]>([]);

  // live list of ALL jobs (all sites) — drives the global navbar indicators below
  readonly jobs$ = this.jobsSource.asObservable();

  // jobs for one site only. The modal is site-scoped so upload history never bleeds
  // across sites; the global list is retained, so returning to a site still shows its
  // history until it's manually cleared.
  jobsForSite$(siteId: number) {
    return this.jobs$.pipe(map(jobs => jobs.filter(j => j.siteId === siteId)));
  }

  // how many are still queued or in flight — drives the navbar busy indicator (next piece)
  readonly processingCount$ = this.jobs$.pipe(
    map(jobs => jobs.filter(j => j.status === 'queued' || j.status === 'processing').length)
  );

  // jobs parked awaiting an overwrite confirmation — drives the prompt badge on the navbar
  readonly pendingConfirmationCount$ = this.jobs$.pipe(
    map(jobs => jobs.filter(j => j.status === 'needsConfirmation').length)
  );

  constructor(private billService: BillService) {}

  // ingestion messages can contain empty/whitespace-only entries (e.g. a blank Summary
  // segment) — drop them here so the modal never renders blank rows
  private static cleanLines(lines: unknown): string[] {
    return (Array.isArray(lines) ? lines : [])
      .map(l => (typeof l === 'string' ? l.trim() : ''))
      .filter(l => l.length > 0);
  }

  // add one or more files for a site, then start the pump if it's idle
  enqueue(files: File[], siteId: number): void {
    for (const file of files) {
      const job: BillUploadJob = {
        id: this.nextId++,
        fileName: file.name,
        siteId,
        status: 'queued',
        billType: null,
        anyWritten: false,
        written: [],
        warnings: [],
        notices: [],
        conflicts: [],
        error: null
      };
      this.items.push({ job, file });
    }
    this.publish();
    this.pump();
  }

  // drop finished (done/error) jobs FOR THIS SITE only; anything queued, in flight, or
  // awaiting confirm stays, and other sites' history is untouched.
  clearFinished(siteId: number): void {
    this.items = this.items.filter(i =>
      i.job.siteId !== siteId ||
      i.job.status === 'queued' || i.job.status === 'processing' || i.job.status === 'needsConfirmation');
    this.publish();
  }

  // user declined the overwrite: mark cancelled (nothing written). Clear completed removes it.
  cancel(id: number): void {
    const item = this.items.find(i => i.job.id === id);
    if (!item || item.job.status !== 'needsConfirmation') return;
    this.patch(id, { status: 'cancelled', conflicts: [] });
  }

  // user confirmed an overwrite: commit the already-extracted bill (force write, no re-OCR)
  confirmOverwrite(id: number): void {
    const item = this.items.find(i => i.job.id === id);
    if (!item || item.job.status !== 'needsConfirmation') return;

    this.patch(id, { status: 'processing' });
    this.billService.commitBill$(item.extraction, item.job.siteId, item.job.fileName).subscribe({
      next: (response) => {
        const ing = response?.ingestion;
        this.patch(id, {
          status: 'done',
          anyWritten: !!ing?.anyWritten,
          written: BillUploadQueueService.cleanLines(ing?.written),
          warnings: BillUploadQueueService.cleanLines(ing?.warnings),
          notices: BillUploadQueueService.cleanLines(ing?.notices),
          conflicts: []
        });
      },
      error: () => this.patch(id, { status: 'error', error: 'Overwrite failed.' })
    });
  }

  // process exactly one queued job at a time
  private pump(): void {
    if (this.processing) return;
    const item = this.items.find(i => i.job.status === 'queued');
    if (!item) return;

    this.processing = true;
    this.patch(item.job.id, { status: 'processing' });

    this.billService.uploadBill$(item.file, item.job.siteId).subscribe({
      next: (response) => {
        const ing = response?.ingestion;
        if (ing?.needsConfirmation) {
          // keep the extraction so a confirm can commit without re-running OCR
          item.extraction = response?.extraction ?? null;
          this.patch(item.job.id, {
            status: 'needsConfirmation',
            billType: ing?.billType ?? null,
            conflicts: BillUploadQueueService.cleanLines(ing?.conflicts),
            warnings: BillUploadQueueService.cleanLines(ing?.warnings),
            notices: BillUploadQueueService.cleanLines(ing?.notices)
          });
        } else {
          this.patch(item.job.id, {
            status: 'done',
            billType: ing?.billType ?? null,
            anyWritten: !!ing?.anyWritten,
            written: BillUploadQueueService.cleanLines(ing?.written),
            warnings: BillUploadQueueService.cleanLines(ing?.warnings),
            notices: BillUploadQueueService.cleanLines(ing?.notices)
          });
        }
        this.finishStep();
      },
      error: () => {
        this.patch(item.job.id, {
          status: 'error',
          error: 'Extraction or save failed for this bill.'
        });
        this.finishStep();
      }
    });
  }

  // free the pump and move to the next queued job
  private finishStep(): void {
    this.processing = false;
    this.pump();
  }

  private patch(id: number, changes: Partial<BillUploadJob>): void {
    const item = this.items.find(i => i.job.id === id);
    if (item) {
      // new object so OnPush views pick up the change
      item.job = { ...item.job, ...changes };
      this.publish();
    }
  }

  // push a fresh snapshot of the job view-models to subscribers
  private publish(): void {
    this.jobsSource.next(this.items.map(i => i.job));
  }
}
